import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PhoneFields from "@/components/PhoneFields.jsx";
import ConfirmDialog from "@/components/ConfirmDialog.jsx";
import RowActionsMenu from "@/components/RowActionsMenu.jsx";
import BackLink from "@/components/BackLink.jsx";
import CustomerListFilterToolbox from "@/components/CustomerListFilterToolbox.jsx";
import {
    getAdminCustomer,
    getAdminCustomerCart,
    getAdminCustomerLikedItems,
    searchAdminCustomerList,
    setAdminCustomerAccountStatus,
    updateAdminCustomer,
    uploadFile
} from "@/api/admin.api.js";
import ProductCatalogGrid from "@/components/ProductCatalogGrid.jsx";
import "@/components/product-catalog-grid.css";
import "@/components/product-filter.css";
import { EMPTY_CUSTOMER_LIST_FILTERS } from "@/utils/customerListFilters.js";
import {
    ACCOUNT_STATUSES,
    ACCOUNT_STATUS_OPTIONS,
    CUSTOMER_TYPES,
    formatAccountStatus,
    formatCustomerType,
    normalizePhones
} from "@/utils/customerProfile.js";
import Price from "@/components/Price.jsx";
import { productImageUrl } from "@/utils/format.js";
import { useAuth } from "@/auth/AuthContext.jsx";
import { canDeleteCustomerAccount } from "@/utils/staffAccess.js";
import ImageUploadSaveHint from "@/components/ImageUploadSaveHint.jsx";
import "@/pages/cart/cart.css";

function getCustomerDisplayName(customer) {
    if (customer.customerType === CUSTOMER_TYPES.CORPORATION && customer.companyName) {
        return customer.companyName;
    }
    return `${customer.firstName} ${customer.lastName}`.trim();
}

function createCustomerForm(customer) {
    return {
        firstName: customer.firstName || "",
        lastName: customer.lastName || "",
        companyName: customer.companyName || "",
        registrationNumber: customer.registrationNumber || "",
        taxId: customer.taxId || "",
        email: customer.email || "",
        accountStatus: customer.accountStatus || ACCOUNT_STATUSES.ACTIVE,
        photoId: customer.photoId || null,
        phones: normalizePhones(customer.phones)
    };
}

function getQuickStatusActions(currentStatus, allowDelete) {
    switch (currentStatus) {
        case ACCOUNT_STATUSES.ACTIVE: {
            const actions = [
                { label: "Désactiver", status: ACCOUNT_STATUSES.DEACTIVATED }
            ];
            if (allowDelete) {
                actions.push({ label: "Marquer supprimé", status: ACCOUNT_STATUSES.DELETED, secondary: true });
            }
            return actions;
        }
        case ACCOUNT_STATUSES.DEACTIVATED:
        case ACCOUNT_STATUSES.CREATED:
            return [{ label: "Activer", status: ACCOUNT_STATUSES.ACTIVE, primary: true }];
        case ACCOUNT_STATUSES.DELETED:
            return allowDelete
                ? [{ label: "Réactiver", status: ACCOUNT_STATUSES.ACTIVE, primary: true }]
                : [];
        default:
            return [];
    }
}

export default function AdminCustomersSection({ setMessage, setError }) {
    const { role } = useAuth();
    const allowCustomerDelete = canDeleteCustomerAccount(role);
    const [, setSearchParams] = useSearchParams();
    const [draftFilters, setDraftFilters] = useState(EMPTY_CUSTOMER_LIST_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(EMPTY_CUSTOMER_LIST_FILTERS);
    const [customers, setCustomers] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(true);
    const loadRequestIdRef = useRef(0);
    const [detailLoading, setDetailLoading] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [viewMode, setViewMode] = useState("list");
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [photoUploading, setPhotoUploading] = useState(false);
    const [pendingImageSave, setPendingImageSave] = useState(false);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [pendingConfirm, setPendingConfirm] = useState(null);
    const [cartItems, setCartItems] = useState([]);
    const [cartLoading, setCartLoading] = useState(false);
    const [cartError, setCartError] = useState("");
    const [favoriteProducts, setFavoriteProducts] = useState([]);
    const [favoritesLoading, setFavoritesLoading] = useState(false);
    const [favoritesError, setFavoritesError] = useState("");

    const loadCustomers = useCallback(async (filters) => {
        const requestId = ++loadRequestIdRef.current;
        setLoading(true);
        setError("");
        try {
            const response = await searchAdminCustomerList({
                search: filters.search.trim() || undefined,
                accountStatus: filters.accountStatus || undefined,
                customerType: filters.customerType || undefined,
                page: 0,
                size: 50
            });
            if (requestId !== loadRequestIdRef.current) {
                return;
            }
            setCustomers(response.data || []);
            setTotalElements(response.totalElements ?? (response.data || []).length);
        } catch (err) {
            if (requestId !== loadRequestIdRef.current) {
                return;
            }
            setError(err.message);
            setCustomers([]);
            setTotalElements(0);
        } finally {
            if (requestId === loadRequestIdRef.current) {
                setLoading(false);
            }
        }
    }, [setError]);

    useEffect(() => {
        if (viewMode !== "list") {
            return;
        }
        loadCustomers(appliedFilters);
    }, [loadCustomers, appliedFilters, viewMode]);

    const applyFilters = async (nextFilters) => {
        const filtersToApply = nextFilters ?? draftFilters;
        if (nextFilters) {
            setDraftFilters(nextFilters);
        }
        setAppliedFilters({ ...filtersToApply });
    };

    const resetFilters = async () => {
        setDraftFilters(EMPTY_CUSTOMER_LIST_FILTERS);
        setAppliedFilters(EMPTY_CUSTOMER_LIST_FILTERS);
    };

    const openCustomerView = async (username) => {
        setDetailLoading(true);
        setCartLoading(true);
        setFavoritesLoading(true);
        setMessage("");
        setError("");
        setCartError("");
        setFavoritesError("");
        setCartItems([]);
        setFavoriteProducts([]);
        try {
            const [customerResponse, cartResponse, favoritesResponse] = await Promise.all([
                getAdminCustomer(username),
                getAdminCustomerCart(username).catch((err) => {
                    setCartError(err.message || "Impossible de charger le panier.");
                    return { data: [] };
                }),
                getAdminCustomerLikedItems(username).catch((err) => {
                    setFavoritesError(err.message || "Impossible de charger les J'aime.");
                    return { data: [] };
                })
            ]);
            setSelectedCustomer(customerResponse.data);
            setCartItems(cartResponse.data || []);
            setFavoriteProducts(favoritesResponse.data || []);
            setViewMode("view");
        } catch (err) {
            setError(err.message);
        } finally {
            setDetailLoading(false);
            setCartLoading(false);
            setFavoritesLoading(false);
        }
    };

    const startEdit = () => {
        if (!selectedCustomer) {
            return;
        }
        setForm(createCustomerForm(selectedCustomer));
        setPendingImageSave(false);
        setMessage("");
        setError("");
        setViewMode("edit");
    };

    const cancelEdit = () => {
        setViewMode("view");
        setForm(null);
        setPhotoUploading(false);
        setPendingImageSave(false);
        setError("");
    };

    const backToList = () => {
        setSelectedCustomer(null);
        setForm(null);
        setViewMode("list");
        setPhotoUploading(false);
        setPendingImageSave(false);
        setCartItems([]);
        setCartError("");
        setError("");
    };

    const handlePhotoChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file || !form) {
            return;
        }
        setPhotoUploading(true);
        setMessage("");
        setError("");
        try {
            const response = await uploadFile(file);
            setForm((current) => ({ ...current, photoId: response.fileId }));
            setPendingImageSave(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setPhotoUploading(false);
            event.target.value = "";
        }
    };

    const removePhoto = () => {
        setForm((current) => ({ ...current, photoId: null }));
    };

    const saveCustomer = async (event) => {
        event.preventDefault();
        if (!selectedCustomer || !form) {
            return;
        }
        setSaving(true);
        setMessage("");
        setError("");
        try {
            const response = await updateAdminCustomer(selectedCustomer.username, {
                firstName: form.firstName.trim(),
                lastName: form.lastName.trim(),
                email: form.email.trim() || null,
                companyName: selectedCustomer.customerType === CUSTOMER_TYPES.CORPORATION
                    ? form.companyName.trim()
                    : undefined,
                registrationNumber: selectedCustomer.customerType === CUSTOMER_TYPES.CORPORATION
                    ? form.registrationNumber.trim() || undefined
                    : undefined,
                taxId: selectedCustomer.customerType === CUSTOMER_TYPES.CORPORATION
                    ? form.taxId.trim() || undefined
                    : undefined,
                accountStatus: form.accountStatus,
                photoId: form.photoId || undefined,
                phones: form.phones.map((phone) => ({
                    phoneNumber: phone.phoneNumber.trim(),
                    label: phone.label?.trim() || undefined
                }))
            });
            setSelectedCustomer(response.data);
            setViewMode("view");
            setForm(null);
            setPendingImageSave(false);
            setMessage("Client mis à jour");
            await loadCustomers(appliedFilters);
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const applyAccountStatus = async (accountStatus) => {
        if (!selectedCustomer) {
            return;
        }
        setUpdatingStatus(true);
        setMessage("");
        setError("");
        try {
            const response = await setAdminCustomerAccountStatus(selectedCustomer.username, accountStatus);
            setSelectedCustomer(response.data);
            setMessage(`Statut mis à jour : ${formatAccountStatus(accountStatus)}`);
            await loadCustomers(appliedFilters);
        } catch (err) {
            setError(err.message);
        } finally {
            setUpdatingStatus(false);
            setPendingConfirm(null);
        }
    };

    const requestAccountStatusChange = (accountStatus, label) => {
        if (accountStatus === ACCOUNT_STATUSES.DELETED || accountStatus === ACCOUNT_STATUSES.DEACTIVATED) {
            setPendingConfirm({
                title: "Confirmer l'action",
                message: `Voulez-vous vraiment ${label.toLowerCase()} ce compte ?`,
                confirmLabel: label,
                dangerous: accountStatus === ACCOUNT_STATUSES.DELETED,
                onConfirm: () => applyAccountStatus(accountStatus)
            });
            return;
        }

        applyAccountStatus(accountStatus);
    };

    const viewCustomerOrders = () => {
        if (!selectedCustomer?.username) {
            return;
        }
        setMessage("");
        setError("");
        setSearchParams({ section: "orders", customer: selectedCustomer.username });
    };

    const buildCustomerMenuItems = () => {
        if (!selectedCustomer) {
            return [];
        }

        const items = [
            {
                id: "orders",
                label: "Voir ses commandes",
                onClick: viewCustomerOrders
            },
            {
                id: "edit",
                label: "Modifier",
                onClick: startEdit
            }
        ];

        getQuickStatusActions(selectedCustomer.accountStatus, allowCustomerDelete).forEach((action) => {
            items.push({
                id: action.status,
                label: action.label,
                danger: action.status === ACCOUNT_STATUSES.DELETED,
                onClick: () => requestAccountStatusChange(action.status, action.label)
            });
        });

        return items;
    };

    if (viewMode === "list") {
        return (
            <section className="card profile-section profile-section--catalog">
                <div className="catalog-with-filters">
                    <CustomerListFilterToolbox
                        filters={draftFilters}
                        loading={loading}
                        onChange={setDraftFilters}
                        onApply={applyFilters}
                        onReset={resetFilters}
                    />

                    <div className="catalog-results">
                        <div className="catalog-results-header">
                            <h2>Clients</h2>
                            <p className="muted catalog-results-count admin-list-hint">
                                {!loading && totalElements > 0 && (
                                    <>
                                        {totalElements} client{totalElements > 1 ? "s" : ""} · cliquez sur une ligne ou le menu ⋮ pour voir le détail
                                    </>
                                )}
                                {!loading && totalElements === 0 && "Aucun client ne correspond à votre recherche."}
                            </p>
                        </div>

                        {loading && <p className="muted">Chargement des clients...</p>}
                        {detailLoading && <p className="muted">Chargement du client...</p>}

                        {!loading && customers.length > 0 && (
                            <div className="admin-table">
                                {customers.map((customer) => {
                                    const displayName = getCustomerDisplayName(customer);

                                    return (
                                        <div key={customer.username} className="admin-row admin-list-row">
                                            <button
                                                type="button"
                                                className="admin-list-row-main"
                                                onClick={() => openCustomerView(customer.username)}
                                            >
                                                <div className="admin-row-body">
                                                    <strong>{displayName}</strong>
                                                    <p className="muted">
                                                        {customer.username}
                                                        {" · "}
                                                        {formatCustomerType(customer.customerType)}
                                                        {" · "}
                                                        <span className={`account-status account-status--${customer.accountStatus?.toLowerCase()}`}>
                                                            {formatAccountStatus(customer.accountStatus)}
                                                        </span>
                                                        {customer.primaryPhone ? ` · ${customer.primaryPhone}` : ""}
                                                    </p>
                                                </div>
                                                <span className="admin-list-row-chevron" aria-hidden="true">›</span>
                                            </button>
                                            <RowActionsMenu
                                                ariaLabel={`Actions pour ${displayName}`}
                                                items={[
                                                    {
                                                        id: "view",
                                                        label: "Voir le détail",
                                                        onClick: () => openCustomerView(customer.username)
                                                    }
                                                ]}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </section>
        );
    }

    if (viewMode === "edit" && selectedCustomer && form) {
        const isCorporation = selectedCustomer.customerType === CUSTOMER_TYPES.CORPORATION;

        return (
            <section className="card profile-section">
                <div className="section-header">
                    <h2>Modifier le client</h2>
                </div>

                <form onSubmit={saveCustomer}>
                    <div className="profile-photo-field">
                        {productImageUrl(form.photoId) ? (
                            <img
                                src={productImageUrl(form.photoId)}
                                alt=""
                                className="profile-photo-preview"
                            />
                        ) : (
                            <div className="profile-photo-preview profile-photo-preview--empty">Photo</div>
                        )}
                        <label className="btn btn-secondary btn-inline profile-photo-upload">
                            {photoUploading ? "Téléversement..." : "Changer la photo"}
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handlePhotoChange}
                                disabled={photoUploading}
                                hidden
                            />
                        </label>
                        {form.photoId && (
                            <button
                                type="button"
                                className="btn btn-secondary btn-inline"
                                onClick={removePhoto}
                            >
                                Retirer la photo
                            </button>
                        )}
                    </div>
                    <ImageUploadSaveHint
                        visible={pendingImageSave}
                        plural={false}
                        saveLabel="Enregistrer"
                    />

                    <dl className="profile-detail-view profile-detail-view--compact">
                        <div className="profile-detail-row">
                            <dt>Nom d'utilisateur</dt>
                            <dd>{selectedCustomer.username}</dd>
                        </div>
                        <div className="profile-detail-row">
                            <dt>Type de compte</dt>
                            <dd>{formatCustomerType(selectedCustomer.customerType)}</dd>
                        </div>
                    </dl>

                    {isCorporation && (
                        <>
                            <label className="field">
                                Raison sociale
                                <input
                                    value={form.companyName}
                                    onChange={(e) => setForm({ ...form, companyName: e.target.value })}
                                    required
                                />
                            </label>
                            <label className="field">
                                N° RCCM (optionnel)
                                <input
                                    value={form.registrationNumber}
                                    onChange={(e) => setForm({ ...form, registrationNumber: e.target.value })}
                                />
                            </label>
                            <label className="field">
                                NIU (optionnel)
                                <input
                                    value={form.taxId}
                                    onChange={(e) => setForm({ ...form, taxId: e.target.value })}
                                />
                            </label>
                        </>
                    )}

                    <label className="field">
                        {isCorporation ? "Prénom du contact" : "Prénom"}
                        <input
                            value={form.firstName}
                            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                            required
                        />
                    </label>
                    <label className="field">
                        {isCorporation ? "Nom du contact" : "Nom"}
                        <input
                            value={form.lastName}
                            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                            required
                        />
                    </label>
                    <label className="field">
                        E-mail (optionnel)
                        <input
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            maxLength={255}
                            autoComplete="email"
                        />
                    </label>

                    <PhoneFields
                        idPrefix="admin-customer-phone"
                        phones={form.phones}
                        onChange={(phones) => setForm({ ...form, phones })}
                    />

                    <label className="field field--status">
                        Statut du compte
                        <select
                            value={form.accountStatus}
                            onChange={(e) => setForm({ ...form, accountStatus: e.target.value })}
                        >
                            {ACCOUNT_STATUS_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </label>

                    <div className="profile-form-actions">
                        <button className="btn btn-primary btn-inline" type="submit" disabled={saving}>
                            {saving ? "Enregistrement..." : "Enregistrer"}
                        </button>
                        <button type="button" className="btn btn-secondary btn-inline" onClick={cancelEdit}>
                            Annuler
                        </button>
                    </div>
                </form>
            </section>
        );
    }

    if (!selectedCustomer) {
        return null;
    }

    const isCorporation = selectedCustomer.customerType === CUSTOMER_TYPES.CORPORATION;

    return (
        <>
        <section className="card profile-section">
            <div className="section-header">
                <div>
                    <BackLink onClick={backToList} className="purchase-detail-back">
                        Retour à la liste
                    </BackLink>
                    <h2>Détails du client</h2>
                </div>
                <RowActionsMenu
                    ariaLabel="Actions du client"
                    items={buildCustomerMenuItems()}
                />
            </div>

            <div className="profile-photo-field">
                {productImageUrl(selectedCustomer.photoId) ? (
                    <img
                        src={productImageUrl(selectedCustomer.photoId)}
                        alt=""
                        className="profile-photo-preview"
                    />
                ) : (
                    <div className="profile-photo-preview profile-photo-preview--empty">Photo</div>
                )}
            </div>

            <dl className="profile-detail-view">
                <div className="profile-detail-row">
                    <dt>Nom d'utilisateur</dt>
                    <dd>{selectedCustomer.username}</dd>
                </div>
                <div className="profile-detail-row">
                    <dt>Statut du compte</dt>
                    <dd>
                        <span className={`account-status account-status--${selectedCustomer.accountStatus?.toLowerCase()}`}>
                            {formatAccountStatus(selectedCustomer.accountStatus)}
                        </span>
                    </dd>
                </div>
                <div className="profile-detail-row">
                    <dt>Type de compte</dt>
                    <dd>{formatCustomerType(selectedCustomer.customerType)}</dd>
                </div>
                {isCorporation && (
                    <>
                        <div className="profile-detail-row">
                            <dt>Raison sociale</dt>
                            <dd>{selectedCustomer.companyName || "—"}</dd>
                        </div>
                        <div className="profile-detail-row">
                            <dt>N° RCCM</dt>
                            <dd>{selectedCustomer.registrationNumber || "—"}</dd>
                        </div>
                        <div className="profile-detail-row">
                            <dt>NIU</dt>
                            <dd>{selectedCustomer.taxId || "—"}</dd>
                        </div>
                    </>
                )}
                <div className="profile-detail-row">
                    <dt>{isCorporation ? "Prénom du contact" : "Prénom"}</dt>
                    <dd>{selectedCustomer.firstName || "—"}</dd>
                </div>
                <div className="profile-detail-row">
                    <dt>{isCorporation ? "Nom du contact" : "Nom"}</dt>
                    <dd>{selectedCustomer.lastName || "—"}</dd>
                </div>
                <div className="profile-detail-row">
                    <dt>E-mail</dt>
                    <dd>{selectedCustomer.email || "—"}</dd>
                </div>
                <div className="profile-detail-row">
                    <dt>Commandes passées</dt>
                    <dd>{selectedCustomer.orderCount ?? 0}</dd>
                </div>
                <div className="profile-detail-row">
                    <dt>Numéros de téléphone</dt>
                    <dd>
                        {selectedCustomer.phones?.length ? (
                            <ul className="profile-phone-view-list">
                                {selectedCustomer.phones.map((phone, index) => (
                                    <li key={`${phone.id || phone.phoneNumber}-${index}`}>
                                        {phone.phoneNumber}
                                        {phone.label ? ` (${phone.label})` : ""}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            "—"
                        )}
                    </dd>
                </div>
            </dl>
        </section>

        <section className="card profile-section admin-customer-cart">
            <div className="section-header">
                <h2>Panier actuel</h2>
                {!cartLoading && !cartError && cartItems.length > 0 && (
                    <p className="muted catalog-results-count">
                        {cartItems.length} article{cartItems.length > 1 ? "s" : ""}
                    </p>
                )}
            </div>

            {cartLoading && <p className="muted">Chargement du panier...</p>}
            {!cartLoading && cartError && <p className="error">{cartError}</p>}
            {!cartLoading && !cartError && cartItems.length === 0 && (
                <p className="muted">Le panier de ce client est vide.</p>
            )}
            {!cartLoading && !cartError && cartItems.length > 0 && (
                <>
                    <div className="cart-list">
                        {cartItems.map((item) => (
                            <article key={item.productId} className="card cart-row">
                                {productImageUrl(item.photoId) ? (
                                    <img
                                        src={productImageUrl(item.photoId)}
                                        alt=""
                                        className="cart-row-thumb"
                                    />
                                ) : (
                                    <div className="cart-row-thumb cart-row-thumb--empty" aria-hidden="true" />
                                )}
                                <div className="cart-row-body">
                                    <h3>{item.title}</h3>
                                    <p className="muted">{item.category}</p>
                                    <p><Price value={item.unitPrice} /></p>
                                </div>
                                <div className="cart-row-actions admin-customer-cart-line-meta">
                                    <p>
                                        Qté <strong>{item.quantity}</strong>
                                    </p>
                                    <p>
                                        <strong><Price unitPrice={item.unitPrice} quantity={item.quantity} /></strong>
                                    </p>
                                </div>
                            </article>
                        ))}
                    </div>
                    <p className="cart-total">
                        Total du panier :{" "}
                        <strong>
                            <Price
                                value={cartItems.reduce((sum, item) => sum + Number(item.lineTotal || 0), 0)}
                            />
                        </strong>
                    </p>
                </>
            )}
        </section>

        <section className="card profile-section admin-customer-favorites">
            <div className="section-header">
                <h2>J&apos;aime</h2>
                {!favoritesLoading && !favoritesError && favoriteProducts.length > 0 && (
                    <p className="muted catalog-results-count">
                        {favoriteProducts.length} produit{favoriteProducts.length > 1 ? "s" : ""}
                    </p>
                )}
            </div>

            {favoritesLoading && <p className="muted">Chargement des J&apos;aime...</p>}
            {!favoritesLoading && favoritesError && <p className="error">{favoritesError}</p>}
            {!favoritesLoading && !favoritesError && favoriteProducts.length === 0 && (
                <p className="muted">Aucun produit dans « J&apos;aime » pour ce client.</p>
            )}
            {!favoritesLoading && !favoritesError && favoriteProducts.length > 0 && (
                <ProductCatalogGrid products={favoriteProducts} showFavoriteHeart={false} />
            )}
        </section>

        <ConfirmDialog
            open={!!pendingConfirm}
            title={pendingConfirm?.title}
            message={pendingConfirm?.message}
            confirmLabel={pendingConfirm?.confirmLabel}
            dangerous={pendingConfirm?.dangerous}
            onConfirm={pendingConfirm?.onConfirm}
            onCancel={() => setPendingConfirm(null)}
        />
        </>
    );
}
